import {
    DocumentTextIcon,
    WrenchIcon,
    PlayIcon,
    CheckCircleIcon,
} from '../../automationStudioIcons';
import { getTotalSteps } from '../../../AutomationStudio/utils';
import styles from '../../../../styles/automationStudio/automationStudio.module.scss';

/**
 * StateIndicator - Shows current panel context to user
 * Displays icon + text based on current state
 */
export default function StateIndicator({
    state,
    selectedStep = null,
    instructions = [],
    isExecuting = false,
    testCompleted = false,
    testCaseKey = null,
    replayExecuting = false,
    replayCurrentStepIndex = null,
    flatStepsForReplay = []
}) {
    const getStateInfo = () => {
        switch (state) {
            case 'INSTRUCTIONS_VIEW':
                return {
                    icon: <DocumentTextIcon className={styles.stateIcon} />,
                    text: testCaseKey ? `Managing Instructions (${testCaseKey})` : "Managing Instructions",
                    color: "#64748B"
                };

            case 'STEP_EDITOR_VIEW':
                if (!selectedStep) {
                    return {
                        icon: <WrenchIcon className={styles.stateIcon} />,
                        text: "Step Editor",
                        color: "#64748B"
                    };
                }

                const totalSteps = getTotalSteps(instructions);
                let stepNumber = 0;
                if (typeof selectedStep.stepIndex === 'number') {
                    stepNumber = selectedStep.stepIndex + 1;
                } else {
                    let flat = 0;
                    for (const inst of instructions) {
                        for (const s of inst.aiSteps || []) {
                            flat += 1;
                            if (s.id === selectedStep.id) {
                                stepNumber = flat;
                                break;
                            }
                        }
                        if (stepNumber) break;
                    }
                }

                return {
                    icon: <WrenchIcon className={styles.stateIcon} />,
                    text: `Editing Step ${stepNumber} of ${totalSteps}`,
                    color: "#2563EB"
                };

            case 'EXECUTION_VIEW':
                if (isExecuting) {
                    const totalSteps = flatStepsForReplay?.length ?? instructions.reduce((sum, inst) => sum + (inst.aiSteps?.length || 0), 0);
                    const hasValidReplayIndex = replayCurrentStepIndex != null && replayCurrentStepIndex >= 0;
                    const currentStepNum = hasValidReplayIndex ? replayCurrentStepIndex + 1 : null;
                    const stepText = replayExecuting && currentStepNum != null && totalSteps > 0
                        ? `Executing step ${currentStepNum} of ${totalSteps}`
                        : "Executing Automation...";
                    return {
                        icon: <PlayIcon className={styles.stateIcon} />,
                        text: stepText,
                        color: "#2563EB"
                    };
                }

                if (testCompleted) {
                    return {
                        icon: <DocumentTextIcon className={styles.stateIcon} />,
                        text: "Execution Results",
                        color: "#64748B"
                    };
                }

                return {
                    icon: <CheckCircleIcon className={styles.stateIcon} />,
                    text: "Execution Results",
                    color: "#64748B"
                };

            default:
                return {
                    icon: <DocumentTextIcon className={styles.stateIcon} />,
                    text: "Automation Studio",
                    color: "#64748B"
                };
        }
    };

    const stateInfo = getStateInfo();

    return (
        <div className={styles.stateIndicator}>
            <div style={{ color: stateInfo.color }}>
                {stateInfo.icon}
            </div>
            <span
                className={`${styles.stateText} ${styles.asTypoSubtitle2}`}
                style={{ color: stateInfo.color }}
                title={stateInfo.text}
            >
                {stateInfo.text}
            </span>
        </div>
    );
}
